import Vue from 'vue'
import commonMutations from '@/store/ui/common/mutations'
import { findIndex, cloneDeep, filter, size, head, toNumber, isNil, get, isEmpty } from 'lodash'

const mutations = {
    ...commonMutations,

    setExpandedNodesIds(state, { id, action }) {
        let index
        if (action === 'expand') {
            index = size(state.expandedNodesIds)
            Vue.set(state.expandedNodesIds, index, id)
        } else if (action === 'collapse') {
            index = findIndex(state.expandedNodesIds, nodeId => nodeId === id)
            Vue.delete(state.expandedNodesIds, index)
        }
    },

    resetExpandedNodesIds(state) {
        Vue.set(state, 'expandedNodesIds', [])
    },

    setCriterion(state, { type, name }) {
        Vue.set(state, 'menuCriteriaType', type)
        Vue.set(state, 'currentCriterionName', name ? name : null)
    },

    setCriterionInBox(state, { criterion }) {
        const index = size(state.formula[state.focusedBox.type][state.focusedBox.index].criteria)
        Vue.set(state.formula[state.focusedBox.type][state.focusedBox.index].criteria, index, criterion)
    },

    setCriteriaInBox(state, { criteria }) {
        let index = size(state.formula[state.focusedBox.type][state.focusedBox.index].criteria)
        for (const criterion of criteria) {
            Vue.set(state.formula[state.focusedBox.type][state.focusedBox.index].criteria, index, criterion)
            index++
        }
    },

    setNewBoxInFormula(state, { boxType }) {
        state.formula[boxType].push({
            volume: null,
            isDisabled: false,
            criteria: []
        })
        Vue.set(state.focusedBox, 'type', boxType)
        Vue.set(state.focusedBox, 'index', boxType === 'inclusion' ? size(state.formula[boxType]) - 1 : 0)
    },

    setVolumes(state, { volumes }) {
        Vue.set(state.metrics, 'volumes', volumes)
    },

    setEmptyFormulaVolumes(state) {
        const formula = cloneDeep(state.formula)
        for (const inclusion of formula.inclusion) {
            inclusion.volume = null
            for (const criterion of inclusion.criteria) {
                criterion.volume = null
            }
        }
        for (const exclusion of formula.exclusion) {
            exclusion.volume = null
            for (const criterion of exclusion.criteria) {
                criterion.volume = null
            }
        }
        Vue.set(state, 'formula', formula)
    },

    setFormulaVolumes(state, { volumes }) {
        const formula = cloneDeep(state.formula)
        const volumesInclusions = volumes.filter(child => child.operator === 'union')
        const volumesExclusions = volumes.filter(child => child.operator === 'exclusion')

        /* eslint-disable no-unused-vars*/

        if (!isEmpty(volumesInclusions)) {
            //real index to keep looping in case a criteria box is empty
            let inclusionIndex = 0
            //index of filled criteria boxes (criteria statistics response only gives volumes for filled criteria boxes)
            let fakeInclusionIndex = 0

            for (const inclusion of formula.inclusion) {
                if (isEmpty(inclusion.criteria) || inclusion.isDisabled) {
                    if (inclusion.isDisabled) inclusion.volume = null
                    inclusionIndex++
                } else {
                    //real index to keep looping in case a criteria is disabled
                    let criterionIndex = 0
                    //index of active criteria
                    let fakeCriterionIndex = 0
                    inclusion.volume = volumesInclusions[fakeInclusionIndex].volume
                    for (const criterion of inclusion.criteria) {
                        if (criterion.criterion.isDisabled) {
                            criterionIndex++
                        } else {
                            criterion.volume = volumesInclusions[fakeInclusionIndex].children[fakeCriterionIndex].volume
                            fakeCriterionIndex++
                            criterionIndex++
                        }
                    }
                    fakeInclusionIndex++
                    inclusionIndex++
                }
            }
        }
        if (!isEmpty(volumesExclusions)) {
            const exclusion = formula.exclusion[0]
            if (!exclusion.isDisabled) {
                exclusion.volume = volumesExclusions[0].children[0].volume
                let criterionIndex = 0
                let fakeCriterionIndex = 0
                for (const criterion of exclusion.criteria) {
                    if (criterion.criterion.isDisabled) {
                        criterionIndex++
                    } else {
                        criterion.volume = volumesExclusions[0].children[0].children[fakeCriterionIndex].volume
                        criterionIndex++
                        fakeCriterionIndex++
                    }
                }
            } else {
                exclusion.volume = null
            }
        }
        Vue.set(state, 'formula', formula)
        /* eslint-enable */
    },

    setSummary(state, { summary }) {
        Vue.set(state.metrics, 'summary', summary)
    },

    setBreakdown(state, { breakdown }) {
        Vue.set(state.metrics, 'breakdown', breakdown)
    },

    setFocusedBox(state, { boxIndex, boxType }) {
        Vue.set(state.focusedBox, 'index', boxIndex)
        Vue.set(state.focusedBox, 'type', boxType)
    },

    setEmptyFormula(state) {
        Vue.set(state.formula, 'inclusion', [
            {
                criteria: [],
                volume: null
            }
        ])
        Vue.set(state.focusedBox, 'index', 0)
        Vue.set(state.focusedBox, 'type', 'inclusion')
        Vue.set(state.formula, 'exclusion', [])
    },

    setDeletedBox(state, { boxIndex, boxType }) {
        Vue.set(
            state.formula,
            `${boxType}`,
            filter(state.formula[boxType], e => e !== state.formula[boxType][boxIndex])
        )
    },

    setEmptyBox(state, { boxIndex, boxType }) {
        Vue.set(state.formula[boxType][boxIndex], 'criteria', [])
        Vue.set(state.formula[boxType][boxIndex], 'volume', null)
    },

    setStateBox(state, { boxState, boxIndex, boxType }) {
        for (const data of state.formula[boxType][boxIndex].criteria) {
            Vue.set(data.criterion, 'active', boxState === 'active')
        }
    },

    setDeletedCriterion(state, { name, boxType, boxIndex }) {
        state.formula[boxType][boxIndex].criteria = filter(state.formula[boxType][boxIndex].criteria, e => e.criterion.name !== name)
    },

    setStateCriterion(state, { name, boxType, boxIndex }) {
        for (const data of state.formula[boxType][boxIndex].criteria) {
            if (data.criterion.name === name) {
                data.criterion.active = !data.criterion.active
            }
        }
    },

    setFormulaSettingsType(state, { settingsType }) {
        Vue.set(state.formulaSettings, 'settingsType', settingsType)
    },

    // Update Global Settings
    setSettingValue(state, { settingName, settingValue }) {
        if (settingName === 'level' || settingName === 'range' || settingName === 'recency') {
            Vue.set(state.formulaSettings.settings, `${settingName}`, toNumber(settingValue))
        } else {
            Vue.set(state.formulaSettings.settings, `${settingName}`, settingValue)
        }
    },

    // Update Criterion Settings
    setCriterionSettingValue(state, { boxIndex, boxType, criterionName, settingName, settingValue }) {
        const criterionIndex = state.formula[boxType][boxIndex].criteria.findIndex(el => el.criterion.name === criterionName)
        Vue.set(state.formula[boxType][boxIndex].criteria[criterionIndex].criterion, settingName, settingValue)
    },

    setCriterionSettingValueByJsonKeyName(state, { jsonKeyName, settingName, settingValue }) {
        for (const type in state.formula) {
            for (const index in state.formula[type]) {
                for (const idx in state.formula[type][index].criteria) {
                    if (state.formula[type][index].criteria[idx].criterion.name === jsonKeyName) {
                        Vue.set(state.formula[type][index].criteria[idx].criterion, `${settingName}`, settingValue)
                    }
                }
            }
        }
    },

    setOneBoxCriterionSettingValueByJsonKeyName(state, { jsonKeyName, settingName, boxType, boxIndex, settingValue }) {
        const formulaBox = get(state, `formula[${boxType}][${boxIndex}].criteria`)
        for (const idx in formulaBox) {
            if (
                //formulaBox[idx].criterion.box.index === boxIndex &&
                //formulaBox[idx].criterion.box.type === boxType &&
                formulaBox[idx].criterion.name === jsonKeyName
            ) {
                Vue.set(formulaBox[idx].criterion, `${settingName}`, settingValue)
            }
        }
    },

    // Update Criteria in formula settings
    setCriteriaInFormulaSettingValue(state, { settingName, settingValue }) {
        for (const type in state.formula) {
            for (const index in state.formula[type]) {
                for (const idx in state.formula[type][index].criteria) {
                    const value = settingName === 'level' || settingName === 'range' || settingName === 'recency' ? toNumber(settingValue) : settingValue
                    if (settingName === 'recency' && state.formula[type][index].criteria[idx].criterion.recency === 395) {
                        continue
                    } else if (settingName === 'recency' && Array.isArray(state.formula[type][index].criteria[idx].criterion.range)) {
                        continue
                    } else if (
                        (settingName === 'range' || settingName === 'level') &&
                        state.formula[type][index].criteria[idx].criterion.type !== 'Sociodemo' &&
                        state.formula[type][index].criteria[idx].criterion.type !== 'Cluster' &&
                        state.formula[type][index].criteria[idx].criterion.type !== 'Segment'
                    ) {
                        continue
                    } else {
                        Vue.set(state.formula[type][index].criteria[idx].criterion, `${settingName}`, value)
                    }
                }
            }
        }
    },

    setDefaultSettings(state) {
        const formulaDefaultSettings = cloneDeep(state.formulaSettings.defaultSettings)
        Vue.set(state.formulaSettings, 'settings', formulaDefaultSettings)
    },

    setCurrentCriterionPath(state, { criterionPath }) {
        Vue.set(state, 'currentCriterionPath', criterionPath)
    },

    setDisplayFormAudience(state, { display, mode, action = 'create' }) {
        Vue.set(state.formAudience, 'display', display)
        Vue.set(state.formAudience, 'mode', mode)
        Vue.set(state.formAudience, 'action', action)
    },

    setActionFormAudience(state, { action }) {
        Vue.set(state.formAudience, 'action', action)
    },

    setDisabledFormulaBox(state, { boxIndex, boxType, isDisabled }) {
        if (!isNil(isDisabled)) {
            Vue.set(state.formula[boxType][boxIndex], 'isDisabled', isDisabled)
        } else {
            Vue.set(state.formula[boxType][boxIndex], 'isDisabled', !state.formula[boxType][boxIndex].isDisabled)
        }
    },

    setDisabledCriteriaInBox(state, { boxIndex, boxType }) {
        for (const data of state.formula[boxType][boxIndex].criteria) {
            Vue.set(data.criterion, 'isDisabled', state.formula[boxType][boxIndex].isDisabled)
        }
    },

    setDisabledCriterion(state, { criterionName, boxIndex, boxType }) {
        const box = state.formula[boxType][boxIndex].criteria
        const data = head(box.filter(el => el.criterion.name === criterionName))
        Vue.set(data.criterion, 'isDisabled', !data.criterion.isDisabled)
    },

    setCcloneFormula(state, { formula }) {
        Vue.set(state.formula, 'cclone', formula)
    },
    setActiveTabBuilder(state, { tab }) {
        Vue.set(state.metrics, 'activeTabBuilder', tab)
    },
    setActiveTabBuilderCclone(state, { tab }) {
        Vue.set(state.cclone, 'activeTab', tab)
    },
    setSelectedVolumeType(state, { volumeType }) {
        Vue.set(state, 'volumeType', volumeType)
    },

    // @TODO: utiliser compo Tabs
    setActiveTabFormAudience(state, { tab }) {
        state.formAudience.activeTabFormAudience = tab
    },

    /**
     *
     * @param {Object} state
     * @param {Boolean} isEditing
     */
    setIsEditingAudience(state, { isEditing }) {
        Vue.set(state, 'isEditingAudience', isEditing)
    },

    /**
     *
     * @param {Object} state
     * @param {Object} node
     */
    setAudienceInEdtion(state, { node }) {
        Vue.set(state, 'audienceInEdition', node)
    },

    /**
     *
     * @param {Object} state
     * @param {Object} node
     */
    setNextAudienceInEdtion(state, node) {
        Vue.set(state, 'nextAudienceInEdition', node)
    },

    /**
     *
     * @param {Object} state
     * @param {String} modalType
     * @param {Boolean} value
     */
    setEditionModal(state, { modalType, value }) {
        Vue.set(state.editionModal, modalType, value)
    },
    /**
     *
     * @param {Object} state
     * @param {Array} newPath
     */
    selectPath(state, newPath){
        state.formula.path = newPath
    },
    /**
     *
     * @param {Object} state
     * @param {String} criteria
     */
    openCriteria(state, criteria){
        state.formula.criteriaSelected = criteria
    }
}

export default mutations
